import React, { type FC } from 'react'

import { useIntl } from 'react-intl'
import { useRecoilCallback, useRecoilValue } from 'recoil'

import {
  ACTION_BUTTON_TYPE_CANCEL,
  ButtonAction,
  DataListExtraColumn,
  DataListHeader,
  DataListRow,
  Flex,
  GRID_VARIANT_SMALL,
  Grid,
  GridCell,
  LINK_VARIANT_DEFAULT,
  Link,
  PaginationBar,
  SKELETON_LOADER_VARIANT_ONWHITE,
  SPACING_8,
  SkeletonLoader,
  SpacingVertical,
  TEXT_VARIANT_ITALIC_LIGHT,
  TEXT_VARIANT_LIGHT,
  TITLE_VARIANT_S,
  Text,
  Title,
  TruncateAndTooltip,
} from 'mailup-web-components'

import { openRecipientsListInGroupNewTab } from 'Helpers/recipient'

import {
  panelAddRecipientChangePaginationCallback,
  panelAddRecipientRemoveGroupCallback,
} from 'Pages/RecipientsList/recoil/callbacks/addRecipient'
import {
  panelAddRecipientGroupsState,
  panelAddRecipientPagedGroupsState,
} from 'Pages/RecipientsList/recoil/state/addRecipient'

const AFTER_WIDTH = '28px'

const GroupsList: FC = () => {
  const intl = useIntl()

  const {
    items: allItems,
    loading,
    pageNumber,
    pageSize,
    orderBy,
    orderDescending,
  } = useRecoilValue(panelAddRecipientGroupsState)

  const items = useRecoilValue(panelAddRecipientPagedGroupsState)

  const handleGroupDelete = useRecoilCallback(
    panelAddRecipientRemoveGroupCallback,
    [],
  )

  const changePagination = useRecoilCallback(
    panelAddRecipientChangePaginationCallback,
    [],
  )

  const heads = [
    {
      label: intl.formatMessage({
        id: 'Load.MailupCommon.Id',
      }),
      span: 3,
      sortable: true,
      field: 'Id',
    },
    {
      label: intl.formatMessage({
        id: 'Load.Common.Group',
      }),
      span: 21,
      sortable: true,
      field: 'Name',
    },
  ]

  const onClickSort = (newOrderBy: 'Id' | 'Name'): void => {
    void changePagination({ orderBy: newOrderBy })
  }

  const handleGroupClick = (e: React.MouseEvent<HTMLLinkElement>): void => {
    const idGroup = e.currentTarget.dataset.value

    if (idGroup !== undefined) {
      openRecipientsListInGroupNewTab(idGroup)
    }
  }

  const handleChangePage = (newPageNumber: number): void => {
    void changePagination({ pageNumber: newPageNumber })
  }
  const handleChangePageSize = (newPageSize: number): void => {
    void changePagination({ pageNumber: 1, pageSize: newPageSize })
  }

  if (allItems.length === 0) {
    return (
      <Flex flexDirection="column" gap="16px">
        <Title variant={TITLE_VARIANT_S}>
          {intl.formatMessage({ id: 'Load.RecipientsList.GroupsMembership' })}
        </Title>
        <Text variant={TEXT_VARIANT_ITALIC_LIGHT}>
          {intl.formatMessage({
            id: 'Load.RecipientsList.GroupsMembershipNoGroups',
          })}
        </Text>
      </Flex>
    )
  }

  return (
    <div>
      <SpacingVertical variant={SPACING_8} />
      <DataListHeader
        heads={heads}
        orderByStatus={orderBy}
        orderDescendingStatus={orderDescending}
        handleChangeColumnsOrderCallback={onClickSort}
        afterWidth={AFTER_WIDTH}
        cols={24}
        gridVariant={GRID_VARIANT_SMALL}
      />
      <section>
        {loading &&
          Array.from({
            length: items.length > 0 ? items.length : pageSize,
          }).map((_, idx) => (
            <DataListRow key={idx}>
              <Grid cols={24} variant={GRID_VARIANT_SMALL}>
                <GridCell span={heads[0]?.span}>
                  <SkeletonLoader variant={SKELETON_LOADER_VARIANT_ONWHITE} />
                </GridCell>
                <GridCell span={heads[1]?.span}>
                  <SkeletonLoader variant={SKELETON_LOADER_VARIANT_ONWHITE} />
                </GridCell>
              </Grid>
              <DataListExtraColumn after width={AFTER_WIDTH}>
                <ButtonAction disabled type={ACTION_BUTTON_TYPE_CANCEL} />
              </DataListExtraColumn>
            </DataListRow>
          ))}
        {!loading &&
          items.map((item) => (
            <DataListRow key={item.Id}>
              <Grid cols={24} variant={GRID_VARIANT_SMALL}>
                <GridCell span={heads[0]?.span}>
                  <Text variant={TEXT_VARIANT_LIGHT}>{item.Id}</Text>
                </GridCell>
                <GridCell span={heads[1]?.span}>
                  <Link
                    variant={LINK_VARIANT_DEFAULT}
                    data-mu-test="group-name"
                    data-value={item.Id}
                    onClick={handleGroupClick}
                  >
                    <TruncateAndTooltip text={item.Name} />
                  </Link>
                </GridCell>
              </Grid>
              <DataListExtraColumn after width={AFTER_WIDTH}>
                <ButtonAction
                  onClick={() => {
                    void handleGroupDelete(item.Id)
                  }}
                  type={ACTION_BUTTON_TYPE_CANCEL}
                />
              </DataListExtraColumn>
            </DataListRow>
          ))}
      </section>

      <PaginationBar
        margin="24px 0 0 0"
        minPageSize={5}
        pageNumber={pageNumber}
        pageSize={pageSize}
        totalItems={allItems.length}
        handleChangePage={handleChangePage}
        handleChangePageSize={handleChangePageSize}
        strings={{
          of: intl.formatMessage({ id: 'Load.Pager.Of' }),
          label: intl.formatMessage({ id: 'Load.Pager.Items' }),
        }}
        data-mu-test="recipient-add__groups-list__pagination"
        formatValues={intl.formatNumber}
      />
    </div>
  )
}

export default GroupsList
